import { useState, type FormEvent } from "react";
import { CloudIcon, ServerIcon } from "lucide-react";

import {
  useCreateCamera,
  useDirectRegister,
  useGeneratePushToken,
} from "@/api/cameras";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import type { ConnectionMode } from "@/types";

interface AddCameraDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fill from a discovered device. */
  preset?: {
    name?: string;
    onvif_host?: string;
    onvif_port?: number;
    manufacturer?: string;
    model?: string;
  };
  defaultSiteId?: string;
}

type Step = "mode" | "form" | "push-url";

export function AddCameraDialog({
  open,
  onOpenChange,
  preset,
  defaultSiteId,
}: AddCameraDialogProps) {
  const [step, setStep] = useState<Step>("mode");
  const [mode, setMode] = useState<ConnectionMode>("agent");

  const [siteId, setSiteId] = useState(defaultSiteId ?? "");
  const [name, setName] = useState(preset?.name ?? "");
  const [onvifHost, setOnvifHost] = useState(preset?.onvif_host ?? "");
  const [onvifPort, setOnvifPort] = useState<number | "">(
    preset?.onvif_port ?? 80,
  );
  const [onvifUser, setOnvifUser] = useState("");
  const [onvifPassword, setOnvifPassword] = useState("");
  const [rtspUrl, setRtspUrl] = useState("");

  const [pushUrl, setPushUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const create = useCreateCamera();
  const direct = useDirectRegister();
  const genToken = useGeneratePushToken();

  function reset() {
    setStep("mode");
    setMode("agent");
    setName("");
    setOnvifHost("");
    setOnvifPort(80);
    setOnvifUser("");
    setOnvifPassword("");
    setRtspUrl("");
    setPushUrl(null);
    setError(null);
  }

  function close(_open: boolean) {
    if (!_open) reset();
    onOpenChange(_open);
  }

  async function onSubmitForm(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    try {
      if (mode === "direct") {
        const cam = await direct.mutateAsync({
          site_id: siteId,
          name,
          manufacturer: preset?.manufacturer,
          model: preset?.model,
        });
        // Mint the push URL right away so the admin sees it as part of
        // the same flow — they only need to copy/paste once.
        const tok = await genToken.mutateAsync(cam.id);
        setPushUrl(tok.push_url);
        setStep("push-url");
      } else {
        await create.mutateAsync({
          site_id: siteId,
          connection_mode: "agent",
          name,
          onvif_host: onvifHost || undefined,
          onvif_port: typeof onvifPort === "number" ? onvifPort : undefined,
          onvif_username: onvifUser || undefined,
          onvif_password: onvifPassword || undefined,
          rtsp_main_url: rtspUrl || undefined,
          manufacturer: preset?.manufacturer,
          model: preset?.model,
        });
        close(false);
      }
    } catch (err) {
      const msg =
        (err as { response?: { data?: { detail?: string } } }).response?.data
          ?.detail ?? "Could not save camera";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add camera</DialogTitle>
          <DialogDescription>
            {step === "mode"
              ? "Choose how this camera connects to SView360."
              : step === "push-url"
                ? "Save the push URL — it will not be shown again."
                : "Provide the device details and credentials."}
          </DialogDescription>
        </DialogHeader>

        {step === "mode" && (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => {
                setMode("agent");
                setStep("form");
              }}
              className={cn(
                "flex w-full items-start gap-3 rounded border p-3 text-left transition hover:bg-muted/40",
              )}
            >
              <ServerIcon className="mt-0.5 h-5 w-5" />
              <div>
                <p className="font-medium">Agent mode</p>
                <p className="text-xs text-muted-foreground">
                  Camera lives on a branch LAN; the local slink-agent talks
                  ONVIF + RTSP and forwards heartbeats. Best for legacy
                  cameras and bank-branch deployments.
                </p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => {
                setMode("direct");
                setStep("form");
              }}
              className={cn(
                "flex w-full items-start gap-3 rounded border p-3 text-left transition hover:bg-muted/40",
              )}
            >
              <CloudIcon className="mt-0.5 h-5 w-5" />
              <div>
                <p className="font-medium">Direct cloud</p>
                <p className="text-xs text-muted-foreground">
                  Camera pushes RTSP straight to the cloud ingress with a
                  token. No agent required. Best for greenfield deployments
                  and modern cameras with stream-out support.
                </p>
              </div>
            </button>
            <DialogFooter>
              <Button variant="outline" onClick={() => close(false)}>
                Cancel
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "form" && (
          <form className="space-y-4" onSubmit={onSubmitForm}>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Mode:</span>
              <Badge variant={mode === "direct" ? "warning" : "online"}>
                {mode === "direct" ? "direct cloud" : "agent"}
              </Badge>
              <button
                type="button"
                className="ml-auto text-xs underline"
                onClick={() => setStep("mode")}
              >
                change
              </button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="site_id">Site ID (UUID)</Label>
              <Input
                id="site_id"
                value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Display name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            {mode === "agent" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="onvif_host">ONVIF host</Label>
                    <Input
                      id="onvif_host"
                      value={onvifHost}
                      onChange={(e) => setOnvifHost(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="onvif_port">ONVIF port</Label>
                    <Input
                      id="onvif_port"
                      type="number"
                      value={onvifPort}
                      onChange={(e) =>
                        setOnvifPort(
                          e.target.value === "" ? "" : Number(e.target.value),
                        )
                      }
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="onvif_user">ONVIF user</Label>
                    <Input
                      id="onvif_user"
                      value={onvifUser}
                      onChange={(e) => setOnvifUser(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="onvif_pwd">ONVIF password</Label>
                    <PasswordInput
                      id="onvif_pwd"
                      value={onvifPassword}
                      onChange={(e) => setOnvifPassword(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rtsp_url">RTSP main URL</Label>
                  <Input
                    id="rtsp_url"
                    value={rtspUrl}
                    onChange={(e) => setRtspUrl(e.target.value)}
                    placeholder="rtsp://host:554/Streaming/Channels/101"
                  />
                </div>
              </>
            )}

            {mode === "direct" && (
              <p className="rounded bg-muted/50 p-3 text-xs text-muted-foreground">
                After save, SView360 will mint a one-time RTSP push URL.
                Configure your camera to push to that URL — no agent or
                ONVIF credentials needed.
              </p>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => close(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={create.isPending || direct.isPending || genToken.isPending}
              >
                {create.isPending || direct.isPending
                  ? "Saving…"
                  : mode === "direct"
                    ? "Save & generate URL"
                    : "Save"}
              </Button>
            </DialogFooter>
          </form>
        )}

        {step === "push-url" && pushUrl && (
          <div className="space-y-3">
            <p className="text-sm">
              <strong>Save this URL.</strong> The token is shown once.
            </p>
            <div className="flex gap-2">
              <input
                readOnly
                value={pushUrl}
                className="flex-1 rounded border bg-muted/40 px-2 py-1 font-mono text-xs"
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  void navigator.clipboard?.writeText(pushUrl);
                }}
              >
                Copy
              </Button>
            </div>
            <DialogFooter>
              <Button onClick={() => close(false)}>Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
